import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { CheckCircle2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { REPORT_STATUS_CLASSES } from './report-status';
import type { CragReport } from '../../types/crag-report';

interface CragReportItemProps {
  report: CragReport;
  /** Names the route the report is about. Off inside a single route's list. */
  showClimb?: boolean;
  /** Names the crag. On only where the list spans several — a maintainer's page. */
  showCrag?: boolean;
  className?: string;
}

/**
 * One report, wherever it is read: the crag page, a route's sheet, a
 * maintainer's page, the panel's queue.
 *
 * The resolution note is given its own block rather than appended to the
 * description, because the two are written by different people and only one of
 * them is the answer — "re-bolted on the 12th" is what a climber standing under
 * the route actually needs to see.
 */
export function CragReportItem({ report, showClimb = true, showCrag = false, className }: CragReportItemProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'it' ? it : enUS;

  const reportedAt = format(new Date(report.created_at), 'd MMM yyyy', { locale: dateLocale });
  const reporter = report.reported_by;

  return (
    <article className={cn('space-y-2 py-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={REPORT_STATUS_CLASSES[report.status]}>
          {t(`cragReports.status.${report.status}`)}
        </Badge>
        <Badge variant="outline">{t(`cragReports.categories.${report.category}`)}</Badge>

        {showClimb && report.climb && (
          <span className="truncate text-sm font-medium">
            {report.climb.name}
            {report.climb.grade ? ` · ${report.climb.grade}` : ''}
          </span>
        )}
        {showCrag && report.crag && (
          <span className="truncate text-sm text-muted-foreground">{report.crag.name}</span>
        )}
      </div>

      <p className="whitespace-pre-wrap text-sm">{report.description}</p>

      {report.resolution_message && (
        <div className="flex gap-2 rounded-md bg-green-500/10 p-2 text-sm text-green-800 dark:text-green-300">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="whitespace-pre-wrap">{report.resolution_message}</p>
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {reporter && (
          <>
            <Avatar className="h-5 w-5">
              {reporter.avatar_small_url && <AvatarImage src={reporter.avatar_small_url} alt={reporter.username} />}
              <AvatarFallback className="text-[10px]">
                {reporter.username.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">{reporter.username}</span>
            <span aria-hidden>·</span>
          </>
        )}
        <span>{reportedAt}</span>
      </div>
    </article>
  );
}
